import React, { useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import { Table } from '@fikasio/react-ui-components';
import EmptyTableRow from 'components/UI/EmptyTableRow';
import TrashIcon from 'components/UI/TrashIcon';
import { usePatchProjectMutation } from 'services/projects/api';
import { Project, ProjectMember } from 'services/projects/types';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const memberKey = (members: ProjectMember[]) => (
  members.map(member => member.emailAddress.trim().toLowerCase()).join('\n')
);

interface ProjectMembersProps {
  editedMembers?: ProjectMember[];
  onMembersChange: (members: ProjectMember[]) => void;
  project?: Project;
}

const ProjectMembers = ({
  editedMembers,
  onMembersChange,
  project,
}: ProjectMembersProps) => {
  const { t } = useTranslation();
  const [patchProject] = usePatchProjectMutation();
  const [draft, setDraft] = useState('');
  const lastSavedKey = useRef<string | undefined>(undefined);
  const members = editedMembers ?? project?.members ?? [];

  useEffect(() => {
    if (!project?.id || editedMembers === undefined) {
      return;
    }
    const nextKey = memberKey(editedMembers);
    if (nextKey === memberKey(project.members ?? []) || nextKey === lastSavedKey.current) {
      return;
    }
    lastSavedKey.current = nextKey;
    patchProject({
      id: project.id,
      members: editedMembers,
    });
  }, [editedMembers, patchProject, project?.id, project?.members]);

  const save = (next: ProjectMember[]) => {
    onMembersChange(next);
  };

  const addMember = () => {
    const emailAddress = draft.trim();
    if (!EMAIL_PATTERN.test(emailAddress)) {
      return;
    }
    const normalized = emailAddress.toLowerCase();
    if (members.some(member => member.emailAddress.toLowerCase() === normalized)) {
      setDraft('');
      return;
    }
    save([...members, { emailAddress }]);
    setDraft('');
  };

  return (
    <div>
      <form
        onSubmit={event => {
          event.preventDefault();
          addMember();
        }}
        style={{
          alignItems: 'center',
          display: 'flex',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <input
          aria-label={t('emailAddress')}
          className="form-control"
          onChange={event => setDraft(event.target.value)}
          placeholder={t('emailAddress')}
          required
          style={{ flex: 1 }}
          type="email"
          value={draft}
        />
        <Button type="submit">{t('addMember')}</Button>
      </form>
      <Table>
        <thead>
          <tr>
            <th>{t('emailAddress')}</th>
            <th style={{ width: 35 }} />
          </tr>
        </thead>
        <tbody>
          {
            members.length === 0 && (
              <EmptyTableRow
                colSpan={2}
                message={t('noMembersToDisplay')}
              />
            )
          }
          {
            members.map(member => (
              <tr key={member.emailAddress}>
                <td>{member.emailAddress}</td>
                <td>
                  <button
                    aria-label={`${t('delete')} ${member.emailAddress}`}
                    onClick={() => save(members.filter(item => item.emailAddress !== member.emailAddress))}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                    }}
                    type="button"
                  >
                    <TrashIcon
                      size="1x"
                      style={{ cursor: 'pointer' }}
                    />
                  </button>
                </td>
              </tr>
            ))
          }
        </tbody>
      </Table>
    </div>
  );
};

export default ProjectMembers;
